/**
 * Seletor de idioma da interface.
 *
 * A troca é aplicada imediatamente (RF05) e só depois persistida no perfil do
 * usuário. Se a gravação falhar, a interface permanece no idioma escolhido e o
 * usuário é avisado — a preferência continua valendo neste dispositivo.
 *
 * Duas variantes:
 *   - <SeletorIdiomaCampo />  campo de formulário (Minha Conta)
 *   - <SeletorIdiomaNav />    itens de dropdown (barra superior, tela de login)
 */
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Dropdown, Form, NavDropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from '../../i18n/config';
import { idiomaAtual } from '../../i18n';
import { alterarIdioma } from '../../i18n/preferencia';

export function SeletorIdiomaCampo({ label = true, disabled = false }) {
    const { t } = useTranslation(['common']);
    const [salvando, setSalvando] = useState(false);
    const atual = idiomaAtual();

    const aoMudar = async (evento) => {
        const codigo = evento.target.value;

        if (codigo === atual) {
            return;
        }

        setSalvando(true);

        try {
            await alterarIdioma(codigo);
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Form.Group>
            {label && <Form.Label>{t('common:language.label')}</Form.Label>}
            <Form.Select
                value={atual}
                onChange={aoMudar}
                disabled={disabled || salvando}
                aria-label={t('common:language.label')}
            >
                {SUPPORTED_LANGUAGES.map((idioma) => (
                    <option key={idioma.code} value={idioma.code}>
                        {idioma.label}
                    </option>
                ))}
            </Form.Select>
            <Form.Text className="text-muted">{t('common:language.help')}</Form.Text>
        </Form.Group>
    );
}

SeletorIdiomaCampo.propTypes = {
    /** Exibe o rótulo acima do campo. */
    label: PropTypes.bool,
    disabled: PropTypes.bool,
};

export function SeletorIdiomaNav() {
    const { t } = useTranslation(['common']);
    const atual = idiomaAtual();

    return (
        <>
            <NavDropdown.Divider />
            <NavDropdown.Header>{t('common:language.label')}</NavDropdown.Header>
            {SUPPORTED_LANGUAGES.map((idioma) => (
                <NavDropdown.Item
                    key={idioma.code}
                    active={idioma.code === atual}
                    onClick={() => alterarIdioma(idioma.code)}
                >
                    {idioma.label}
                </NavDropdown.Item>
            ))}
        </>
    );
}

/**
 * Variante compacta para telas públicas (login, redefinição de senha), onde ainda
 * não existe barra de navegação. Fica ancorada no canto superior direito para não
 * interferir no layout das telas de login personalizadas por programa.
 */
export function SeletorIdiomaCompacto() {
    const { t } = useTranslation(['common']);
    const atual = idiomaAtual();
    const atualLabel =
        SUPPORTED_LANGUAGES.find((idioma) => idioma.code === atual)?.shortLabel || atual;

    return (
        <div
            style={{ position: 'fixed', top: '1rem', right: '1rem', zIndex: 1030 }}
        >
            <Dropdown align="end">
                <Dropdown.Toggle
                    variant="light"
                    size="sm"
                    id="seletor-idioma-publico"
                    aria-label={t('common:language.label')}
                >
                    {atualLabel}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    {SUPPORTED_LANGUAGES.map((idioma) => (
                        <Dropdown.Item
                            key={idioma.code}
                            active={idioma.code === atual}
                            onClick={() => alterarIdioma(idioma.code)}
                        >
                            {idioma.label}
                        </Dropdown.Item>
                    ))}
                </Dropdown.Menu>
            </Dropdown>
        </div>
    );
}

export default SeletorIdiomaCampo;
